import { createContext, useContext, useState, useEffect } from "react";
import type { User } from "@/lib/schema";
import { getSyncService } from "@/services/sync-service";
import { getDeviceManager } from "@/services/device-manager";

// API base URL - HARD-CODED to Railway backend (no more URL confusion)
const API_BASE_URL = 'https://main-veritus-api-production.up.railway.app';
const ACCOUNTSIQ_URL = 'https://accounts.acroniq.co.uk';

interface AuthContextType {
  user: User | null;
  login: () => void;
  register: () => void;
  logout: () => Promise<void>;
  isLoading: boolean;
  requiresPasswordChange: boolean;
  clearPasswordChangeRequirement: () => void;
  syncInitialized: boolean;
  syncError: string | null;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [requiresPasswordChange, setRequiresPasswordChange] = useState(false);
  const [syncInitialized, setSyncInitialized] = useState(false);
  const [syncError, setSyncError] = useState<string | null>(null);

  useEffect(() => {
    checkAuth();
  }, []);

  async function checkAuth() {
    try {
      // Check if user is authenticated via session
      const res = await fetch(`${API_BASE_URL}/api/auth/me`, {
        credentials: "include",
      });

      if (res.ok) {
        const data = await res.json();
        setUser(data.user);
      }
    } catch (error) {
      console.error('Auth check failed:', error);
    } finally {
      setIsLoading(false);
    }
  }

  async function initializeSync() {
    // STABILITY MODE: All sync features disabled
    setSyncInitialized(false);
    setSyncError(null);
    console.log('🔒 STABILITY MODE: All sync features disabled');
  }

  function login() {
    // Redirect to AccountsIQ login
    const returnUrl = `${window.location.origin}/auth/callback`;
    window.location.href = `${ACCOUNTSIQ_URL}/login?redirect=${encodeURIComponent(returnUrl)}`;
  }

  function register() {
    // Redirect to AccountsIQ registration
    const returnUrl = `${window.location.origin}/auth/callback`;
    window.location.href = `${ACCOUNTSIQ_URL}/register?redirect=${encodeURIComponent(returnUrl)}`;
  }

  async function logout() {
    try {
      await fetch(`${API_BASE_URL}/api/auth/logout`, {
        method: "POST",
        credentials: "include",
      });
    } catch (error) {
      // Continue with logout even if API call fails
    }
    
    // Cleanup sync service
    try {
      const syncService = getSyncService();
      syncService.cleanup();
      
      const deviceManager = getDeviceManager();
      deviceManager.clearDeviceData();
      
      setSyncInitialized(false);
      setSyncError(null);
    } catch (error) {
      console.error('Error cleaning up sync service:', error);
    }
    
    setUser(null);
    setRequiresPasswordChange(false);
  }

  function clearPasswordChangeRequirement() {
    setRequiresPasswordChange(false);
  }

  return (
    <AuthContext.Provider value={{ 
      user, 
      login, 
      register, 
      logout, 
      isLoading, 
      requiresPasswordChange, 
      clearPasswordChangeRequirement,
      syncInitialized,
      syncError
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
